import {Node} from 'slate';
import zukeeper from 'zukeeper';
import {create} from 'zustand';

import {validateNode} from '../../pages/reportBuilder/utils/nodeHelpers';
import * as documentStoreAdapter from '../../services/modules/ReportBuilder/dataAdapters/documentStoreAdapter';

import {useConfigStore} from './configStore';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'modified' | 'error';

export interface DocumentMetadata {
  documentId: string | null;
  title: string;
  author: string;
  framework: string;
  createdAt: string;
  updatedAt: string;
}

export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BlockSize {
  width: number;
  height: number;
}

export interface WatermarkConfig {
  text: string;
  color: string;
  fontSize: number;
  opacity: number;
  layout: 'diagonal' | 'horizontal';
  fontFamily: string;
}

export interface DocumentState {
  // Core Document Data
  content: Node[];
  pages: Node[][];
  pageOrientation: 'portrait' | 'landscape';
  pageMargins: PageMargins;
  watermark: WatermarkConfig;

  // Metadata
  metadata: DocumentMetadata;

  // Status
  saveStatus: SaveStatus;

  // UI/Editor Preferences
  theme: string;
  autoSave: boolean;
  autoSaveInterval: number;
  showGridLines: boolean;
  snapToGrid: boolean;
  gridSize: number;
  defaultBlockSize: BlockSize;
}

export interface DocumentActions {
  // Content & Structure
  setContent: (content: Node[]) => void;
  setPages: (pages: Node[][]) => void;

  // Page Settings
  setPageOrientation: (pageOrientation: 'portrait' | 'landscape') => void;
  setPageMargins: (pageMargins: PageMargins) => void;
  setWatermark: (watermark: WatermarkConfig) => void;

  // Metadata
  setMetadata: (metadata: Partial<DocumentMetadata>) => void;

  // Document Operations
  resetDocument: () => void;
  loadDocument: (data: Partial<DocumentState>) => void;
  saveDocument: () => Promise<void>;
  loadInitialDocument: (docId?: string) => Promise<void>;

  // Preferences
  setTheme: (theme: string) => void;
  toggleAutoSave: () => void;
  setAutoSaveInterval: (interval: number) => void;
  toggleGridLines: () => void;
  toggleSnapToGrid: () => void;
  setGridSize: (size: number) => void;
  setDefaultBlockSize: (size: BlockSize) => void;
  resetPreferences: () => void;

  // Status
  setSaveStatus: (saveStatus: SaveStatus) => void;
}

export type DocumentStore = DocumentState & DocumentActions;

const getInitialState = (): DocumentState => {
  const config = useConfigStore.getState();

  return {
    content: [
      {type: 'paragraph', align: 'left', children: [{text: ''}]} as any
    ],
    pages: [],
    pageOrientation: config.pageOrientation,
    pageMargins: config.pageMargins,
    watermark: config.watermark as WatermarkConfig,
    metadata: {
      documentId: null,
      title: 'Untitled Document',
      author: '',
      framework: 'brsr',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    saveStatus: 'idle',
    theme: 'light',
    autoSave: true,
    autoSaveInterval: 3000,
    showGridLines: false,
    snapToGrid: false,
    gridSize: 20,
    defaultBlockSize: config.blockSize
  };
};

// module-level variable to cancel all others //

let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleAutoSave = (get: () => DocumentStore) => {
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(() => {
    void (async () => {
      const state = get();
      const docId = state.metadata.documentId;
      if (!docId) return; // Don't auto-save if there's no ID yet
      const {...stateToSave} = state;
      state.setSaveStatus('saving');
      const result = await documentStoreAdapter.saveDocument(
        docId,
        stateToSave
      );
      state.setSaveStatus(result ? 'saved' : 'error');
    })();
  }, 1500);
};

const useDocumentStore = create<DocumentStore>(
  zukeeper(
    (
      set: (fn: (state: DocumentStore) => Partial<DocumentStore>) => void,
      get: () => DocumentStore
    ) => ({
      ...getInitialState(),
      setContent: (content: Node[]) => {
        set((state) => ({
          content,
          metadata: {
            ...state.metadata,
            updatedAt: new Date().toISOString()
          }
        }));
        if (get().autoSave) scheduleAutoSave(get);
      },

      setPages: (pages: Node[][]) => {
        set((state) => ({
          pages,
          metadata: {
            ...state.metadata,
            updatedAt: new Date().toISOString()
          }
        }));
      },

      setPageOrientation: (pageOrientation: 'portrait' | 'landscape') => {
        set((state) => ({
          pageOrientation,
          metadata: {
            ...state.metadata,
            updatedAt: new Date().toISOString()
          }
        }));
        if (get().autoSave) scheduleAutoSave(get);
      },

      setPageMargins: (pageMargins: PageMargins) => {
        set((state) => ({
          pageMargins,
          metadata: {
            ...state.metadata,
            updatedAt: new Date().toISOString()
          }
        }));
        if (get().autoSave) scheduleAutoSave(get);
      },

      setWatermark: (watermark: WatermarkConfig) => {
        set((state) => ({
          watermark,
          metadata: {
            ...state.metadata,
            updatedAt: new Date().toISOString()
          }
        }));
        if (get().autoSave) scheduleAutoSave(get);
      },

      setMetadata: (metadata: Partial<DocumentMetadata>) => {
        set((state) => ({
          metadata: {
            ...state.metadata,
            ...metadata,
            updatedAt: new Date().toISOString()
          }
        }));
        if (get().autoSave) scheduleAutoSave(get);
      },

      resetDocument: () => {
        set(() => ({
          ...getInitialState(),
          metadata: {
            documentId: null,
            title: 'Untitled Document',
            author: '',
            framework: 'brsr',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          saveStatus: 'idle'
        }));
      },

      loadDocument: (data: Partial<DocumentState>) => {
        const actionKeys = new Set([
          'setContent',
          'setPages',
          'setPageOrientation',
          'setPageMargins',
          'setWatermark',
          'setMetadata',
          'resetDocument',
          'loadDocument',
          'saveDocument',
          'loadInitialDocument',
          'setTheme',
          'toggleAutoSave',
          'setAutoSaveInterval',
          'toggleGridLines',
          'toggleSnapToGrid',
          'setGridSize',
          'setDefaultBlockSize',
          'resetPreferences',
          'setSaveStatus'
        ]);
        const safe = Object.fromEntries(
          Object.entries(data as object).filter(([k]) => !actionKeys.has(k))
        );
        set((state) => ({...state, ...safe}));
      },

      saveDocument: async (): Promise<void> => {
        const state = get();
        const docId = state.metadata.documentId;

        if (!docId) {
          console.warn('[documentStore] Cannot save — no documentId set.');
          set(() => ({saveStatus: 'error'}));

          return;
        }

        set(() => ({saveStatus: 'saving'}));

        // Strip runtime-only fields before persisting
        const {...stateToSave} = state;

        const result = await documentStoreAdapter.saveDocument(
          docId,
          stateToSave
        );
        set(() => ({saveStatus: result ? 'saved' : 'error'}));
      },

      loadInitialDocument: async (docId?: string): Promise<void> => {
        const id = docId ?? get().metadata.documentId ?? 'default';

        const loaded = await documentStoreAdapter.loadDocument(id);

        if (!loaded) return;

        if (!loaded.content || !Array.isArray(loaded.content)) {
          console.warn('[documentStore] Invalid content format detected.');

          return;
        }

        const validatedContent = loaded.content.map(validateNode);
        const validatedPages = Array.isArray(loaded.pages)
          ? loaded.pages.map((page: Node[] | any) =>
              Array.isArray(page)
                ? page.map(validateNode)
                : [
                    {
                      type: 'paragraph',
                      children: [{text: ''}]
                    } as any
                  ]
            )
          : [validatedContent];

        set((state) => ({
          ...state,
          ...loaded,
          content: validatedContent,
          pages: validatedPages
        }));
      },

      setTheme: (theme: string) => {
        set(() => ({theme}));
      },

      toggleAutoSave: () => {
        set((state) => ({autoSave: !state.autoSave}));
      },

      setAutoSaveInterval: (autoSaveInterval: number) => {
        set(() => ({autoSaveInterval}));
      },

      toggleGridLines: () => {
        set((state) => ({showGridLines: !state.showGridLines}));
      },

      toggleSnapToGrid: () => {
        set((state) => ({snapToGrid: !state.snapToGrid}));
      },

      setGridSize: (gridSize: number) => {
        set(() => ({gridSize}));
      },

      setDefaultBlockSize: (defaultBlockSize: BlockSize) => {
        set(() => ({defaultBlockSize}));
      },

      resetPreferences: () => {
        const config = useConfigStore.getState();
        set(() => ({
          theme: 'light',
          autoSave: true,
          autoSaveInterval: 3000,
          showGridLines: false,
          snapToGrid: false,
          gridSize: 20,
          defaultBlockSize: config.blockSize
        }));
      },
      setSaveStatus: (saveStatus: SaveStatus) => {
        set(() => ({saveStatus}));
      }
    })
  )
);

// Optional: Expose store globally for debugging
(window as any).documentStore = useDocumentStore;

export default useDocumentStore;
